// src/components/common/AlertSystem.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimes, FaExclamationCircle } from 'react-icons/fa';

// Create Alert Context
const AlertContext = createContext();

// Alert Provider Component
export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);

  const addAlert = useCallback((message, type = 'info', duration = 5000, title = null) => {
    const id = Date.now() + Math.random();
    const alert = {
      id,
      message,
      type, // 'success', 'error', 'warning', 'info'
      title,
      duration,
      timestamp: new Date()
    };

    setAlerts(prev => [...prev, alert]);

    // Auto remove alert after duration
    if (duration > 0) {
      setTimeout(() => {
        removeAlert(id);
      }, duration);
    }

    return id;
  }, []);

  const removeAlert = useCallback((id) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id));
  }, []);

  const clearAllAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  const success = useCallback((message, title, duration) => addAlert(message, 'success', duration, title), [addAlert]);
  const error = useCallback((message, title, duration) => addAlert(message, 'error', duration || 0, title), [addAlert]);
  const warning = useCallback((message, title, duration) => addAlert(message, 'warning', duration, title), [addAlert]);
  const info = useCallback((message, title, duration) => addAlert(message, 'info', duration, title), [addAlert]);

  const contextValue = useCallback(() => ({
    alerts,
    addAlert,
    removeAlert,
    clearAllAlerts,
    success,
    error,
    warning,
    info
  }), [alerts, addAlert, removeAlert, clearAllAlerts, success, error, warning, info]);

  return (
    <AlertContext.Provider value={contextValue()}>
      {children}
      <AlertContainer />
    </AlertContext.Provider>
  );
};

// Hook to use alerts
export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

// Alert Container Component
const AlertContainer = () => {
  const { alerts, removeAlert } = useAlert();

  if (alerts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '1rem',
      right: '1rem',
      zIndex: 9999,
      maxWidth: '400px',
      width: '100%'
    }}>
      <div className="d-flex flex-column gap-2">
        {alerts.map(alert => (
          <Alert
            key={alert.id}
            alert={alert}
            onClose={() => removeAlert(alert.id)}
          />
        ))}
      </div>
    </div>
  );
};

// Individual Alert Component
const Alert = ({ alert, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    // Slide in animation
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const handleClose = () => {
    setIsRemoving(true);
    setTimeout(() => onClose(), 300); // Wait for animation
  };

  const getAlertStyles = () => {
    let bgColor, borderColor, textColor;
    
    switch (alert.type) {
      case 'success':
        bgColor = '#d1e7dd';
        borderColor = '#badbcc';
        textColor = '#0f5132';
        break;
      case 'error':
        bgColor = '#f8d7da';
        borderColor = '#f5c2c7';
        textColor = '#842029';
        break;
      case 'warning':
        bgColor = '#fff3cd';
        borderColor = '#ffecb5';
        textColor = '#664d03';
        break;
      case 'info':
      default:
        bgColor = '#cff4fc';
        borderColor = '#b6effb';
        textColor = '#055160';
        break;
    }

    return {
      position: 'relative',
      padding: '1rem',
      borderRadius: '0.375rem',
      border: `1px solid ${borderColor}`,
      backgroundColor: bgColor,
      color: textColor,
      boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
      transform: isRemoving ? 'translateX(100%)' : isVisible ? 'translateX(0)' : 'translateX(100%)',
      opacity: isRemoving ? 0 : isVisible ? 1 : 0,
      transition: 'all 0.3s ease-in-out'
    };
  };

  const getIcon = () => {
    const iconProps = { className: "h-5 w-5 flex-shrink-0" };
    
    switch (alert.type) {
      case 'success':
        return <FaCheckCircle {...iconProps} className="h-5 w-5 flex-shrink-0 text-green-400" />;
      case 'error':
        return <FaExclamationCircle {...iconProps} className="h-5 w-5 flex-shrink-0 text-red-400" />;
      case 'warning':
        return <FaExclamationTriangle {...iconProps} className="h-5 w-5 flex-shrink-0 text-yellow-400" />;
      case 'info':
      default:
        return <FaInfoCircle {...iconProps} className="h-5 w-5 flex-shrink-0 text-blue-400" />;
    }
  };

  return (
    <div style={getAlertStyles()}>
      <div className="d-flex align-items-start">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        <div className="ms-3 flex-grow-1">
          {alert.title && (
            <h6 className="mb-1 fw-medium" style={{ fontSize: '0.875rem' }}>
              {alert.title}
            </h6>
          )}
          <p className="mb-0" style={{ fontSize: '0.875rem' }}>
            {alert.message}
          </p>
          {alert.timestamp && (
            <p className="mb-0 mt-1" style={{ fontSize: '0.75rem', opacity: 0.75 }}>
              {alert.timestamp.toLocaleTimeString()}
            </p>
          )}
        </div>
        <div className="ms-3 flex-shrink-0">
          <button
            onClick={handleClose}
            className="btn btn-sm p-1"
            style={{ 
              background: 'transparent',
              border: 'none',
              opacity: 0.7,
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
          >
            <FaTimes style={{ width: '1rem', height: '1rem' }} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Standalone Alert component for inline usage
export const InlineAlert = ({ 
  type = 'info', 
  title, 
  message, 
  onClose, 
  className = '',
  icon = true 
}) => {
  const getAlertStyles = () => {
    let alertClass = 'alert ';
    
    switch (type) {
      case 'success':
        alertClass += 'alert-success';
        break;
      case 'error':
        alertClass += 'alert-danger';
        break;
      case 'warning':
        alertClass += 'alert-warning';
        break;
      case 'info':
      default:
        alertClass += 'alert-info';
        break;
    }

    return `${alertClass} ${className}`;
  };

  const getIcon = () => {
    if (!icon) return null;
    
    const iconProps = { className: "h-5 w-5 flex-shrink-0" };
    
    switch (type) {
      case 'success':
        return <FaCheckCircle {...iconProps} className="h-5 w-5 flex-shrink-0 text-green-400" />;
      case 'error':
        return <FaExclamationCircle {...iconProps} className="h-5 w-5 flex-shrink-0 text-red-400" />;
      case 'warning':
        return <FaExclamationTriangle {...iconProps} className="h-5 w-5 flex-shrink-0 text-yellow-400" />;
      case 'info':
      default:
        return <FaInfoCircle {...iconProps} className="h-5 w-5 flex-shrink-0 text-blue-400" />;
    }
  };

  return (
    <div className={getAlertStyles()} role="alert">
      <div className="d-flex">
        {icon && (
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
        )}
        <div className={icon ? "ms-3" : ""}>
          {title && (
            <h6 className="alert-heading mb-1">
              {title}
            </h6>
          )}
          <p className="mb-0">
            {message}
          </p>
        </div>
        {onClose && (
          <div className="ms-auto ps-3">
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            >
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

